/*
  Free shipping threshold bar.

  The cart drawer and the cart page are re-rendered server side by the Section Rendering API, so
  their bars are already correct on every cart change and need no JS. Only the product page bar is
  marked data-live: it re-reads the cart after an add to cart so the line under the button updates
  without a reload.
*/

class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('data-live')) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  refresh() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    fetch(`${cartUrl}.js`, { headers: { 'Content-Type': 'application/json' } })
      .then((response) => response.json())
      .then((cart) => this.render(cart.total_price))
      .catch((error) => console.error(error));
  }

  render(total) {
    const threshold = parseInt(this.dataset.threshold, 10);
    if (!threshold || Number.isNaN(total)) return;

    const remaining = threshold - total;
    let state = 'progress';
    let amount = remaining;

    if (total <= 0) {
      state = 'promise';
      amount = threshold;
    } else if (remaining <= 0) {
      state = 'success';
      amount = threshold;
    }

    const template = this.dataset[
      { promise: 'textPromise', progress: 'textProgress', success: 'textSuccess' }[state]
    ];
    const messageTarget = this.querySelector('[data-free-shipping-message] span');
    const fill = this.querySelector('[data-free-shipping-fill]');

    if (messageTarget) {
      messageTarget.innerHTML = this.escapeHTML(template).replace(
        '[amount]',
        `<strong class="free-shipping-bar__amount">${this.formatMoney(amount)}</strong>`
      );
    }

    if (fill) {
      fill.style.width = `${Math.min(100, total > 0 ? Math.round((total / threshold) * 100) : 0)}%`;
    }

    this.dataset.state = state;
  }

  escapeHTML(string) {
    const div = document.createElement('div');
    div.textContent = string;
    return div.innerHTML;
  }

  // Minimal port of Shopify's money_without_trailing_zeros filter, driven by the shop's own
  // money_format string. Liquid renders the first paint; this only has to match it.
  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return format;

    let value = '';

    switch (placeholder[1]) {
      case 'amount':
        value = this.thousands((cents / 100.0).toFixed(2), ',', '.');
        break;
      case 'amount_no_decimals':
        value = this.thousands(Math.round(cents / 100.0).toString(), ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = this.thousands((cents / 100.0).toFixed(2), '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = this.thousands(Math.round(cents / 100.0).toString(), '.', ',');
        break;
      case 'amount_with_space_separator':
        value = this.thousands((cents / 100.0).toFixed(2), ' ', ',');
        break;
      default:
        value = this.thousands((cents / 100.0).toFixed(2), ',', '.');
    }

    // money_without_trailing_zeros: "$80.00" becomes "$80", "$62.50" stays as it is.
    value = value.replace(/[.,]00$/, '');

    return format.replace(placeholder[0], value).replace(/<[^>]*>/g, '');
  }

  thousands(number, thousandsSeparator, decimalSeparator) {
    const parts = number.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousandsSeparator}`);
    return parts[1] ? `${whole}${decimalSeparator}${parts[1]}` : whole;
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
